import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Alert, Box, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CLIMATE_ZONE_LABELS, RAIN_RECOMMENDATION_UNITS, type RainRecommendationKey } from '../../constants/rainRecommendation';
import { useRainRecommendation } from '../../hooks/useRainRecommendation';
import type { RecommendedRange } from '../../types';
import type { FieldComponentProps } from '../form-engine/types';

const numberFormat = new Intl.NumberFormat();

export const formatRange = ({ min, max }: RecommendedRange, key: RainRecommendationKey) =>
  `${numberFormat.format(min)} - ${numberFormat.format(max)}${RAIN_RECOMMENDATION_UNITS[key] ? ` ${RAIN_RECOMMENDATION_UNITS[key]}` : ''}`;

export const RegionRecommendationHelp = () => {
  const { data } = useRainRecommendation();

  if (!data?.rain_threshold || !data.rain_efficiency || !data.climate_zone || data.climate_zone === 'polar') {
    return null;
  }

  return (
    <Tooltip
      title={
        <Alert severity="info">
          <Typography variant="body2">
            Based on the project coordinates, this location is classified as <b>{CLIMATE_ZONE_LABELS[data.climate_zone]}</b>. For this
            climate, the recommended range is <b>{formatRange(data.rain_efficiency, 'rain_efficiency')}</b> for Rain Cleaning Efficiency and{' '}
            <b>{formatRange(data.rain_threshold, 'rain_threshold')}</b> for Rain Threshold. You can select any value within this range.
            Several values at or near the limits can also be tested for sensitivity analysis.
          </Typography>
        </Alert>
      }
      placement="bottom"
      slotProps={{ tooltip: { sx: { bgcolor: 'transparent', p: 0, maxWidth: 400 } } }}
    >
      <Box
        component="span"
        aria-label="help"
        sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, color: 'inherit', cursor: 'default' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <HelpOutlineOutlinedIcon sx={{ height: '1rem' }} />
      </Box>
    </Tooltip>
  );
};

const RainRecommendationField = ({ field }: FieldComponentProps) => {
  const { data } = useRainRecommendation();
  const key = field.recommendation as RainRecommendationKey;
  const range = data?.[key];

  // No range for polar climates or locations without Koppen-Geiger data.
  if (!range) {
    return null;
  }

  return (
    <Grid size={field.size || 12}>
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          Region recommendation: <b>{formatRange(range, key)}</b>
          <RegionRecommendationHelp />
        </Typography>
      </Box>
    </Grid>
  );
};

export const RainRecommendationMessage = ({ field }: FieldComponentProps) => {
  const { data, isError } = useRainRecommendation();

  if (isError) {
    return (
      <Grid size={field.size || 12}>
        <Alert severity="warning">
          <Typography variant="body2">
            The climate-based recommendation could not be retrieved. Please fill in Rain Threshold and Rain Cleaning Efficiency manually.
          </Typography>
        </Alert>
      </Grid>
    );
  }

  if (!data) {
    return null;
  }

  if (!data.rain_threshold || !data.rain_efficiency || !data.climate_zone || data.climate_zone === 'polar') {
    return (
      <Grid size={field.size || 12}>
        <Alert severity="info">
          <Typography variant="body2">
            No climate-based recommendation is available for this location (polar climate or no climate data). Please fill in Rain Threshold
            and Rain Cleaning Efficiency manually.
          </Typography>
        </Alert>
      </Grid>
    );
  }

  return null;
};

export default RainRecommendationField;
